import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import type { I18nContextValue } from '../../types/i18nContext';

type RenderChatbotConversationPageGateOptions = {
  chatbotId: string | undefined;
  conversationId: string | undefined;
  isChatbotLoading: boolean;
  isConversationLoading: boolean;
  isChatbotError: boolean;
  chatbotError: unknown;
  hasChatbot: boolean;
  isConversationError: boolean;
  hasConversation: boolean;
  translate: I18nContextValue['translate'];
};

/**
 * Returns a gate screen when the route/query is not ready for the transcript.
 * Returns null when the page can render the main conversation UI.
 */
export function renderChatbotConversationPageGate({
  chatbotId,
  conversationId,
  isChatbotLoading,
  isConversationLoading,
  isChatbotError,
  chatbotError,
  hasChatbot,
  isConversationError,
  hasConversation,
  translate,
}: RenderChatbotConversationPageGateOptions): ReactNode {
  if (!chatbotId?.trim() || !conversationId?.trim()) {
    return <Navigate to="/chatbots" replace />;
  }

  if (isChatbotLoading || isConversationLoading) {
    return (
      <LiksPage
        size="big"
        content={<p>{translate('chatbots.conversations.detail.loading')}</p>}
      />
    );
  }

  if (isChatbotError || !hasChatbot) {
    return (
      <LiksPage
        size="big"
        content={
          <p role="alert">
            {chatbotError instanceof Error
              ? chatbotError.message
              : translate('chatbots.loadError')}
          </p>
        }
      />
    );
  }

  if (isConversationError || !hasConversation) {
    return (
      <LiksPage
        size="big"
        content={
          <p role="alert">
            {translate('chatbots.conversations.detail.loadError')}
          </p>
        }
      />
    );
  }

  return null;
}
